import React, { useState, useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { firebaseApp } from "../../../providers/firebase";
import Wysiwyg from "../../custom-ui/old-ui/Wysiwyg";
import { createPostSchema } from "./schema";

import { createNewEntryForEvents, updateEntryForEvents, useGetEventById } from "../../../data/event";
import toast from "react-hot-toast";
import { useAuth } from "../../../providers/auth";
import { Timestamp } from "firebase/firestore";
import { useEffect } from "react";
import ImageUploadEvent from "./ImageUpload_Event";
import ToggleButton from "./Toogle";

const storage = getStorage(firebaseApp);

export default function CreatePostForm({ toggleModal, id = "" }: { toggleModal: () => void, id?: string }) {

  const { user }: any = useAuth();

  const { data: formdata }: any = useGetEventById(id || "")

  const [selectedImages, setSelectedImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [postData, setPostData] = useState()
  const [existImages, setExistImages] = useState()

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  }: any = useForm({
    resolver: zodResolver(createPostSchema),
    defaultValues: {
      title: "",
      status: "active",
      featured: "featured",
      description: formdata?.description || postData?.description || "",
    }
  });


  useEffect(() => {
    if (formdata) {


      reset({
        title: formdata.title || "",
        status: formdata.status || "active",
        featured: formdata.featured || "featured",
        description: formdata.description || "",
      });

      setPostData(formdata);
    }
  }, [formdata, reset]);

  const handleFiles = (files: FileList | null) => {
    if (!files) return;

    const newFiles = Array.from(files).filter((file) =>
      file.type.startsWith("image/")
    );

    if (selectedImages?.length + newFiles?.length + existImages?.length > 5) {
      alert("Maximum 5 images allowed");
      return;
    }

    setSelectedImages((prev: any) => [...prev, ...newFiles]);

    const newPreviewURLs = newFiles.map((file) => URL.createObjectURL(file));
    setPreviewImages((prev: any) => [...prev, ...newPreviewURLs]);
  };

  const onDrop = useCallback(
    (e: any) => {
      e.preventDefault();
      handleFiles(e.dataTransfer.files);
    },
    [selectedImages]
  );

  const onDragOver = (e: any) => e.preventDefault();


  const removeImage = (index: number) => {
    setSelectedImages((prev: any) => prev.filter((_: any, i: any) => i !== index));

    URL.revokeObjectURL(previewImages[index]);
    setPreviewImages((prev: any) => prev.filter((_: any, i: any) => i !== index));
  };

  const handleClearAll = () => {
    previewImages.forEach((url: any) => URL.revokeObjectURL(url));

    setSelectedImages([]);
    setPreviewImages([]);

    reset({
      title: "",
      description: "",
      status: "",
      featured: "",
    });
  };


  const onSubmit = async (data: any) => {
    setIsUploading(true);

    let uploadedUrls: string[] = [];

    if (selectedImages.length > 0) {
      uploadedUrls = await Promise.all(
        selectedImages.map(async (file: any) => {
          const fileRef = ref(storage, `posts/${Date.now()}_${file.name}`);
          const snap = await uploadBytes(fileRef, file);
          return await getDownloadURL(snap.ref);
        })
      );
    }

    const finalData = {
      ...data,
      images: [
        ...(existImages || []), // remaining existing images
        ...uploadedUrls,             // new uploaded images
      ],
    };

    const postDataFinal = {
      ...finalData,
      autor: user?.name || user?.auth?.displayName,
      created_by: user?.name || user?.auth?.displayName,
      updated_at: Timestamp.now(),
      updated_by: user?.name || user?.auth?.displayName,
      isFeatuered: finalData.featured === "featured",
      post_type: "post",
    };

    if (id) {
      updateEntryForEvents(id, postDataFinal)
        .then(() => {
          toggleModal();
          toast.success("Post Updated successfully!");
        })
        .catch(() => {
          setIsUploading(false);
          toast.error("Failed to update post.");
        });

    } else {
      createNewEntryForEvents(postDataFinal)
        .then(() => {
          toggleModal();
          toast.success("Post created successfully!");
        })
        .catch(() => {
          setIsUploading(false);
          toast.error("Failed to create post.");
        });
    }
  };





  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 sm:gap-6 px-1 sm:px-2">
      <div>
        <label className="block mb-2 text-[13px] font-semibold text-[#15415E]">
          Post Title <span className="text-[#ED3535]">*</span>
        </label>
        <Controller
          control={control}
          name="title"
          render={(field) => (
            <input
              type="text"
              className="w-full h-[46px] rounded-[10px] border border-[#D5E0E6] bg-white px-3 text-[14px] text-[#15415E] placeholder:text-[#A5BDC5] outline-none hover:border-[#15415E]/40 focus:ring-2 focus:ring-inset focus:ring-[#15415E]/20"
              placeholder="Enter Post Title"
              {...field}
            />
          )}
          error={errors?.title?.message}
          required
        />
        {errors.title && (
          <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
        <div className="rounded-[10px] border border-[#E8EEF2] bg-[#F8FBFD] px-4 py-3">
          <p className="text-[13px] font-semibold text-[#15415E]">Status</p>
          <Controller
            control={control}
            name="status"
            render={(field) => (
              <ToggleButton
                isOn={field.value === "active"}
                buttonText={field.value === "active" ? "Active" : "Archived"}
                handleClick={() => {
                  field.onChange(field.value === "active" ? "archived" : "active");
                }}
              />
            )}
          />
        </div>
        <div className="rounded-[10px] border border-[#E8EEF2] bg-[#F8FBFD] px-4 py-3">
          <p className="text-[13px] font-semibold text-[#15415E]">Featured</p>
          <Controller
            control={control}
            name="featured"
            render={(field) => (
              <ToggleButton
                isOn={field.value === "featured"}
                buttonText={field.value === "featured" ? "Featured" : "Non-Featured"}
                handleClick={() =>
                  field.onChange(field.value === "featured" ? "non-featured" : "featured")
                }
              />
            )}
          />
        </div>
      </div>

      <div className="create-post-description-editor [&>.relative]:mb-0">
        <label className="block mb-2 text-[13px] font-semibold text-[#15415E]">
          Description <span className="text-[#ED3535]">*</span>
        </label>
        <Controller
          control={control}
          name="description"
          render={(field) => (
            <Wysiwyg
              {...field}
              rows={10}
              readOnly={false}
              value={field?.value}
              placeholder={"Write about post"}
              directions=""
              editorClassName="pl-4 pr-4"
              editorStyle={{
                height: "auto",
                minHeight: "156px",
                maxHeight: "280px",
                overflowY: "auto",
              }}
              error={errors?.description?.message}
              required
            />
          )}
        />
      </div>

      <div
        className="rounded-[10px] border border-dashed border-[#D5E0E6] bg-[#F8FBFD] p-4 sm:p-5"
        onDrop={onDrop}
        onDragOver={onDragOver}
      >
        <ImageUploadEvent
          previewImages={previewImages}
          handleFiles={handleFiles}
          removeImage={removeImage}
          selectedImages={selectedImages}
          uploadedImges={postData?.images}
          setExistImages={setExistImages}
        />
      </div>

      <div className="pt-5 border-t border-[#E8EEF2] flex flex-row items-center gap-3">
        {!id ? (
          <button
            type="button"
            onClick={handleClearAll}
            className="flex-1 h-[44px] rounded-full border border-[#15415E] text-[#15415E] text-[14px] font-semibold bg-white hover:bg-[#F4F8FB] transition-colors duration-200"
          >
            Clear All
          </button>
        ) : (
          <button
            type="button"
            onClick={toggleModal}
            className="flex-1 h-[44px] rounded-full border border-[#15415E] text-[#15415E] text-[14px] font-semibold bg-white hover:bg-[#F4F8FB] transition-colors duration-200"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isUploading}
          className="flex-1 h-[44px] rounded-full text-white bg-[#15415E] text-[14px] font-semibold hover:bg-[#1d5278] focus:outline-none disabled:bg-[#636b71] disabled:cursor-not-allowed transition-colors duration-200"
        >
          {isUploading ? "Uploading..." : "Save Post"}
        </button>
      </div>
    </form>
  );
}
